import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import SegmentedToggle from "../ui/segmented-toggle";
import { useSocial } from "../../contexts/SocialContext";
import { useToast } from "../../contexts/ToastContext";
import { Check, Loader2, Users } from "lucide-react";

const MODE_OPTIONS = [
  { value: "in_person", label: "In person" },
  { value: "virtual", label: "Online" },
];

/**
 * "We trained together", from either end.
 *
 * `variant="tag"` runs on your own post: pick the mates who were there.
 * `variant="claim"` runs on somebody else's: you are the only candidate, so
 * there is nobody to pick and the dialog is just the mode and a confirm.
 *
 * Both send a claim the other person has to accept. Naming who you trained
 * with puts a workout on their profile too, which is not a thing one person
 * should be able to do to another on their own.
 */
export default function TrainedTogetherDialog({
  open,
  onOpenChange,
  variant = "tag",
  post,
}) {
  const { friends, loadFriends, linkSessions } = useSocial();
  const toast = useToast();
  const [mode, setMode] = useState("in_person");
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const isClaim = variant === "claim";

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setQuery("");
    setMode("in_person");
    if (!isClaim) loadFriends();
  }, [open, isClaim, loadFriends]);

  // Whoever is already on the card cannot be added again — the server would
  // reject the duplicate, and offering the option is a promise it won't keep.
  const alreadyLinked = useMemo(
    () => new Set((post?.partners || []).map((p) => Number(p.user_id))),
    [post],
  );

  const candidates = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (friends || [])
      .filter((f) => !alreadyLinked.has(Number(f.id)))
      .filter((f) => !term || (f.name || "").toLowerCase().includes(term));
  }, [friends, alreadyLinked, query]);

  const toggle = useCallback((id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const handleSubmit = async () => {
    const partnerIds = isClaim ? [Number(post.user_id)] : selected;
    if (partnerIds.length === 0) return;
    setSaving(true);

    // One claim per mate, and one failing must not take the others with it —
    // a duplicate on one name is not a reason to drop the rest.
    const results = await Promise.allSettled(
      partnerIds.map((partnerId) =>
        linkSessions({
          partnerId,
          sessionId: isClaim ? null : post?.workout_session_id ?? null,
          partnerSessionId: isClaim ? post?.workout_session_id ?? null : null,
          mode,
        }),
      ),
    );

    const sent = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - sent;
    setSaving(false);

    if (sent > 0) {
      toast.showToast({
        title: isClaim ? "Sent" : sent === 1 ? "Sent" : `Sent to ${sent} mates`,
        description: isClaim
          ? "They'll be asked to confirm you were there."
          : "It shows on both your feeds once they confirm.",
      });
    }
    if (failed > 0) {
      toast.showToast({
        title: "Some didn't send",
        description:
          sent > 0
            ? `${failed} couldn't be linked — they may already be on this workout.`
            : "That workout may already be linked.",
      });
    }
    if (sent > 0) onOpenChange(false);
  };

  const canSubmit = isClaim || selected.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isClaim ? "Were you at this workout?" : "Who did you train with?"}
          </DialogTitle>
          <DialogDescription>
            {isClaim ? (
              <>
                {post?.author_name || "They"} will be asked to confirm. Once
                they do, it shows on both your feeds.
              </>
            ) : (
              <>
                Pick the mates who were there. Each one confirms before it shows
                on their feed.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="together-mode" className="text-xs text-muted-foreground">
              How
            </Label>
            <SegmentedToggle
              id="together-mode"
              value={mode}
              onChange={setMode}
              options={MODE_OPTIONS}
              itemClassName="flex-1"
            />
          </div>

          {!isClaim && (
            <div className="space-y-2">
              {(friends || []).length > 6 && (
                <Input
                  placeholder="Search mates..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="text-sm"
                />
              )}

              {candidates.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-3 py-6 text-center">
                  <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground/60" />
                  <p className="text-sm text-muted-foreground">
                    {(friends || []).length === 0
                      ? "Add mates first — the Mates tab is where they turn up."
                      : query
                        ? "No mates match that."
                        : "Everyone's already on this workout."}
                  </p>
                </div>
              ) : (
                <ul className="max-h-56 overflow-y-auto -mx-1 px-1 space-y-1">
                  {candidates.map((friend) => {
                    const active = selected.includes(friend.id);
                    return (
                      <li key={friend.id}>
                        <button
                          type="button"
                          onClick={() => toggle(friend.id)}
                          aria-pressed={active}
                          className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                            active ? "bg-primary/10" : "hover:bg-foreground/5"
                          }`}
                        >
                          {friend.photo_url ? (
                            <img src={friend.photo_url} alt="" className="h-7 w-7 rounded-full" />
                          ) : (
                            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-medium">
                              {friend.name?.[0] || "?"}
                            </div>
                          )}
                          <span className="flex-1 text-sm truncate">{friend.name}</span>
                          {active && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isClaim
              ? "I was there"
              : selected.length > 1
                ? `Link ${selected.length} mates`
                : "Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
